import { LumenButton } from '@photoshelter/socialie-components';
import useKey from '@rooks/use-key';
import { useUpdateSubmissionMutation } from 'api/submissions';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Submission from 'types/Submission';

interface Props {
  submission: Submission;
  theme?: 'thumbnail' | 'topbar';
  keyboard?: boolean;
}

export default function SubmissionRating(props: Props) {
  const { t } = useTranslation();
  const { submission, keyboard, theme = 'topbar' } = props;
  const [updateSubmission] = useUpdateSubmissionMutation();

  useKey(['ArrowUp'], () => {
    if (keyboard) rateSubmission('starred');
  });

  useKey(['ArrowDown'], () => {
    if (keyboard) rateSubmission('rejected');
  });

  const rateSubmission = (rating: Submission['rating']) => {
    const nextRating = submission.rating === rating ? 'unrated' : rating;
    updateSubmission({ submission, changes: { rating: nextRating } });
  };

  const defaultColor = theme === 'thumbnail' ? 'secondary' : 'outline';

  const starButtonProps = {
    size: 'sm',
    color: submission.rating === 'starred' ? 'success' : defaultColor,
    onClick: () => rateSubmission('starred'),
    leftIcon:
      submission.rating === 'starred' ? 'thumbsup-filled' : 'thumbsup-outline',
  } as const;

  const rejectButtonProps = {
    size: 'sm',
    color: submission.rating === 'rejected' ? 'fail' : defaultColor,
    onClick: () => rateSubmission('rejected'),
    leftIcon:
      submission.rating === 'rejected'
        ? 'thumbsdown-filled'
        : 'thumbsdown-outline',
  } as const;

  if (theme === 'topbar') {
    return (
      <>
        <LumenButton {...starButtonProps}>
          {t('submissionRating.star')}
        </LumenButton>

        <LumenButton {...rejectButtonProps}>
          {t('submissionRating.reject')}
        </LumenButton>
      </>
    );
  }

  return (
    <div className="dark flex items-center gap-0.5">
      <LumenButton
        {...starButtonProps}
        aria-label={t('submissionRating.star')}
        className={clsx(
          submission.rating !== 'starred' &&
            'group-[:not(:hover)_&]:bg-transparent dark:group-[:not(:hover)_&]:bg-transparent'
        )}
      />

      <LumenButton
        {...rejectButtonProps}
        aria-label={t('submissionRating.reject')}
        className={clsx(
          submission.rating !== 'rejected' &&
            'group-[:not(:hover)_&]:bg-transparent dark:group-[:not(:hover)_&]:bg-transparent'
        )}
      />
    </div>
  );
}
